import { Input, InputNumber, Select } from 'antd';
import { CalendarOutlined, LockOutlined, ControlOutlined } from '@ant-design/icons';
import PriceListPicker from './PriceListPicker';

// ── Stage 6Q.1 — "New Proforma" control block (grouped operator console) ──
// The controls column of the editor header strip (Controls | Bill To | Ship To).
// Regrouped from four loose stacked rows into three labelled clusters so it reads
// like a glass-fabrication operator faceplate rather than a flat form:
//   • IDENTITY — Proforma No (machine-issued, mono, accent rail) + Date.
//   • CUTTING TOLERANCE & UNITS — a bordered "faceplate" holding WST mode +
//     W/H tolerance steppers + I/P Unit + Area visibility. The selected WST mode
//     drives the W/H controls (ROUND_OFF → inch-step Select 0/3/6…; PLUS_MM →
//     mm number; even-up modes → disabled dash).
//   • LINE OPTIONS — Other_Fab / Location / Group / Edit Chargeable as toggle
//     chips (blue when on) instead of a checkbox wall.
// Every option list stays master-driven via `dropdowns`; the step / area / unit
// tokens are internal calc enums, never business master data. SQFT/SQMT AREA is
// locked to the company Price UOM (Company Defaults) — shown with a lock glyph.
const WASTAGE_STEPS = [0, 3, 6, 9, 12, 15, 18, 21, 24]; // W/H step options (internal calc enum)
const WASTAGE_LABEL = { PLUS_MM: '(+) MM' }; // display-only override; values stay master-driven
const AREA_TAGS = ['SQFT', 'SQMT', 'RFT', 'RMT'];

function opts(list) {
  return (Array.isArray(list) ? list : []).map((o) => ({ label: o.label, value: o.value }));
}

// Scoped styling — turns the compact AntD controls into the console look without
// touching the global ConfigProvider theme tokens. Blue = #1877F2, slate neutrals.
const CTRL_CSS = `
.pf-ctrl{background:linear-gradient(180deg,#fbfcfe 0%,#f6f9fd 100%);}
/* eyebrow section label with a fading hairline */
.pf-eyebrow{font-size:9.5px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#94a3b8;display:flex;align-items:center;gap:8px;margin-bottom:4px;}
.pf-eyebrow::after{content:"";flex:1;height:1px;background:linear-gradient(90deg,#e2e8f0,transparent);}
/* field micro-label inside the faceplate */
.pf-fk{font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#64748b;}
/* identity cards */
.pf-idcard{background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;display:flex;flex-direction:column;gap:1px;position:relative;overflow:hidden;}
.pf-idcard .pf-k{font-size:9px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#94a3b8;}
.pf-docno{flex:1;min-width:0;}
.pf-docno::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,#1877F2,#6aa8f7);}
.pf-docno .ant-input{border:0!important;box-shadow:none!important;padding:0!important;height:18px;background:transparent!important;font-family:'JetBrains Mono','SF Mono',ui-monospace,Menlo,Consolas,monospace;font-size:13px;font-weight:600;letter-spacing:.02em;color:#94a3b8;}
.pf-date .ant-input{border:0!important;box-shadow:none!important;padding:0!important;height:18px;background:transparent!important;font-size:13px;font-weight:600;color:#1e293b;}
/* ── Cutting-spec plate: the document's governing measurement instrument ──
   A hairline-celled panel, zones stacked in order of authority (mode →
   tolerance/unit → area → options). Every measurement value is set in mono
   tabular figures so the plate reads like a machine readout. */
.pf-plate{background:#fff;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;box-shadow:0 1px 2px rgba(15,30,60,.05);}
.pf-zone{padding:10px 13px;}
.pf-zone + .pf-zone{border-top:1px solid #eef2f7;}
.pf-zlabel{display:block;font-size:8.5px;font-weight:800;letter-spacing:.13em;text-transform:uppercase;color:#94a3b8;margin-bottom:7px;line-height:1;}
.pf-zhead{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:8px;}
.pf-zhead .pf-zlabel{margin-bottom:0;}
/* Zone 1 — governing wastage mode, flagged with a blue authority tick */
.pf-zone-mode{position:relative;padding-left:16px;}
.pf-zone-mode::before{content:"";position:absolute;left:0;top:10px;bottom:10px;width:3px;border-radius:0 3px 3px 0;background:linear-gradient(180deg,#1877F2,#5b9bf5);}
/* Zone 1b — price list. Same anatomy as the mode zone; the tick is amber only
   when the document overrides, so an ordinary document shows no extra colour.
   Label and control share ONE row — the choice is a single short phrase, so
   stacking it wasted a line and read as a heading over a group, not a field. */
.pf-zone-pl{position:relative;padding-left:16px;display:flex;align-items:center;gap:10px;}
.pf-zone-pl .pf-zlabel{margin-bottom:0;flex:0 0 auto;}
.pf-zone-pl::before{content:"";position:absolute;left:0;top:10px;bottom:10px;width:3px;border-radius:0 3px 3px 0;background:#e2e8f0;}
.pf-zone-pl:has(.pf-pl-override)::before{background:linear-gradient(180deg,#F59E0B,#fbbf24);}
.pf-zone-pl:has(.pf-pl-none)::before{background:linear-gradient(180deg,#64748b,#94a3b8);}
/* Wastage mode + W/H tolerance share one line */
.pf-modeline{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-top:1px;}
/* UOM line — area-unit pills + input unit select share one line, vertically
   centred to a common 30px height with a small bottom gap before line options */
.pf-uomline{display:flex;align-items:center;flex-wrap:wrap;gap:8px;padding-bottom:2%;}
.pf-uomline > *{align-self:center;margin:0;}
.pf-uomline .pf-areagroup{min-height:30px;}

/* WST mode select — calm segmented tint (governing, not garish) */
.pf-wst .ant-select-selector{font-size:12.5px!important;height:32px!important;background:#eef4ff!important;border-color:#cdddf7!important;border-radius:8px!important;padding:0 12px!important;display:flex;align-items:center;}
.pf-wst.ant-select:hover .ant-select-selector,.pf-wst.ant-select-focused .ant-select-selector{border-color:#1877F2!important;background:#e7f0ff!important;}
.pf-wst .ant-select-selection-item{font-size:12.5px;font-weight:700;color:#0f5fd6;line-height:30px;letter-spacing:.01em;}
.pf-wst .ant-select-arrow{color:#0f5fd6;}
.pf-wst-pop .ant-select-item{font-size:11px;min-height:26px;line-height:20px;padding:4px 10px;}
.pf-wst-pop .ant-select-item-option-selected{background:#e8f0fe!important;color:#0f5fd6;font-weight:700;}

/* W/H tolerance steppers — mono readouts */
.pf-dim{display:inline-flex;align-items:center;gap:6px;}
.pf-dim .pf-lab{font-size:10px;font-weight:800;color:#64748b;width:10px;text-align:center;font-family:'JetBrains Mono',ui-monospace,Menlo,Consolas,monospace;}
.pf-step .ant-input-number{border:1px solid #cdddf7!important;border-radius:7px!important;height:30px;background:#fff;box-shadow:none!important;}
.pf-step .ant-input-number:hover,.pf-step .ant-input-number-focused{border-color:#1877F2!important;}
.pf-step .ant-input-number-input{height:28px;text-align:center;font-family:'JetBrains Mono','SF Mono',ui-monospace,Menlo,Consolas,monospace;font-size:15px;font-weight:700;color:#0f5fd6;padding:0 2px;font-variant-numeric:tabular-nums;}
.pf-step .ant-input-number-handler-wrap{opacity:1;background:#f2f7ff;border-inline-start-color:#e0ebfb;width:17px;}
.pf-step .ant-input-number-handler{border-color:#e0ebfb;}
.pf-step .ant-input-number-handler-up-inner,.pf-step .ant-input-number-handler-down-inner{color:#0f5fd6;}
/* step-mode Select (ROUND_OFF) mirrors the number look */
.pf-step-sel .ant-select-selector{height:30px!important;border:1px solid #cdddf7!important;border-radius:7px!important;background:#fff!important;padding:0 6px!important;display:flex;align-items:center;justify-content:center;}
.pf-step-sel .ant-select-selection-item{font-family:'JetBrains Mono',ui-monospace,Menlo,Consolas,monospace;font-size:15px;font-weight:700;color:#0f5fd6;line-height:28px;text-align:center;font-variant-numeric:tabular-nums;}
.pf-step-sel .ant-select-arrow{display:none;}
.pf-dash{width:44px;height:30px;display:inline-flex;align-items:center;justify-content:center;border:1px dashed #cbd5e1;border-radius:7px;color:#94a3b8;font-weight:700;background:#f8fafc;}
.pf-unit-chip{font-size:9.5px;font-weight:700;color:#94a3b8;letter-spacing:.06em;align-self:center;font-family:'JetBrains Mono',ui-monospace,Menlo,Consolas,monospace;}

/* Input unit select */
.pf-ipunit{width:82px;}
.pf-ipunit .ant-select-selector{height:30px!important;border:1px solid #cbd5e1!important;border-radius:7px!important;background:#fff!important;padding:0 10px!important;display:flex;align-items:center;}
.pf-ipunit.ant-select:hover .ant-select-selector,.pf-ipunit.ant-select-focused .ant-select-selector{border-color:#1877F2!important;}
.pf-ipunit .ant-select-selection-item{font-size:12.5px;font-weight:700;color:#1e293b;line-height:28px;font-family:'JetBrains Mono',ui-monospace,Menlo,Consolas,monospace;}
.pf-ipunit .ant-select-arrow{color:#94a3b8;}

/* Area + line-option pill tracks, tightly stacked with no labels */
.pf-zone-tracks{display:flex;flex-direction:column;align-items:flex-start;gap:5px;padding:7px 13px;}
/* Area units — connected segmented track (coordinated units) */
.pf-areagroup{display:inline-flex;align-items:center;gap:2px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:3px;}
.pf-areatag{font-size:10.5px;font-weight:700;letter-spacing:.03em;padding:5px 10px;border-radius:6px;color:#94a3b8;cursor:pointer;user-select:none;display:inline-flex;align-items:center;gap:4px;transition:all .12s ease;line-height:1;}
.pf-areatag:not(.on):not(.lock):hover{background:#eef2f7;color:#475569;}
.pf-areatag.on{background:#1877F2;color:#fff;box-shadow:0 1px 2px rgba(24,119,242,.35);}
.pf-areatag.lock{background:#e7f0ff;color:#0f5fd6;cursor:default;box-shadow:inset 0 0 0 1px #cdddf7;}
.pf-areatag.lock .anticon{font-size:9px;opacity:.75;}
.pf-areatag.dis{opacity:.45;cursor:not-allowed;}
.pf-lockedhint{font-size:9px;color:#94a3b8;font-style:italic;letter-spacing:.02em;}

/* Line options reuse the Area-Units pill track (.pf-areagroup / .pf-areatag). */
`;

export default function ProformaControlsPanel({ header, setHeader, dropdowns, dropdownsLoading, dropdownsError, readOnly, onToggleEditChargeable }) {
  const set = (patch) => setHeader((h) => ({ ...h, ...patch }));
  const mode = header.wastageMode || 'ROUND_OFF';
  const isStep = mode === 'ROUND_OFF';
  const isPlus = mode === 'PLUS_MM';
  const areaVisible = Array.isArray(header.areaVisible) ? header.areaVisible : ['SQFT'];
  // STRICT Price-UOM rule: the PRICED area unit is locked to the company Price
  // UOM (Company Defaults). SQM ⇒ SQMT, SQF ⇒ SQFT — it drives the rate/basic and
  // cannot be switched per document. The OPPOSITE area unit (`secondaryArea`) is a
  // DISPLAY-ONLY reference column the operator can hide/show — it never affects
  // pricing. RFT/RMT (perimeter, edge processes) stay toggleable too.
  const uomArea = ['SQM', 'SQMT'].includes(String(header.priceUomCode || '').toUpperCase()) ? 'SQMT' : 'SQFT';
  const secondaryArea = uomArea === 'SQMT' ? 'SQFT' : 'SQMT';
  // Render order: locked priced unit first, then the SQF/SQM reference, then RFT/RMT.
  const areaRenderOrder = [uomArea, secondaryArea, 'RFT', 'RMT'];
  const toggleArea = (tag) => {
    if (readOnly || tag === uomArea) return; // the priced unit is locked
    const on = areaVisible.includes(tag);
    let next = on
      ? areaVisible.filter((t) => t !== tag)
      : AREA_TAGS.filter((t) => areaVisible.includes(t) || t === tag);
    // Keep the priced unit present so the secondary area reads as an ADDITIONAL
    // reference column (both tags present), never a UOM switch.
    if (!next.includes(uomArea)) next = [uomArea, ...next.filter((t) => t !== uomArea)];
    set({ areaVisible: next });
  };
  const wastageOptions = opts(dropdowns.wastageModes).map((o) => ({ value: o.value, label: WASTAGE_LABEL[o.value] || o.label }));

  // W/H tolerance control — mode-dependent. Step mode → Select of inch steps;
  // plus mode → mm number stepper; even-up modes → non-editable dash.
  function wastageCell(field) {
    if (isStep) {
      return (
        <Select size="small" className="pf-step-sel" popupMatchSelectWidth={false} style={{ width: 52 }} value={header[field] || 0} disabled={readOnly}
          options={WASTAGE_STEPS.map((v) => ({ label: String(v), value: v }))}
          onChange={(v) => set({ [field]: v || 0 })} />
      );
    }
    if (isPlus) {
      return (
        <span className="pf-step">
          <InputNumber size="small" style={{ width: 62 }} min={0} controls
            value={header[field] || 0} disabled={readOnly} onChange={(v) => set({ [field]: v || 0 })} />
        </span>
      );
    }
    return <span className="pf-dash">—</span>;
  }

  // ── Line Options — compact 2×2 boxed grid (FAB | GRP / LOC | CHRG) ──
  // Ordered to fill the grid row-major as the mockup: FAB, GRP on top; LOC, CHRG
  // below. Each toggles a header flag; Location and Group stay interlocked (Group
  // forces Location on; clearing Location clears Group). Edit Chargeable routes
  // through onToggleEditChargeable when the parent supplies it. `short` is the
  // cell label; `label` is the full name kept for the hover tooltip.
  const OPTIONS = [
    { key: 'otherFab', label: 'Other Fab', short: 'FAB', checked: header.otherFab === true,
      toggle: (v) => set({ otherFab: v }) },
    { key: 'groupByLoc', label: 'Group', short: 'GRP', checked: header.groupByLoc === true,
      toggle: (v) => set({ groupByLoc: v, showLocation: v ? true : header.showLocation }) },
    { key: 'showLocation', label: 'Location', short: 'LOC', checked: header.showLocation === true,
      toggle: (v) => set({ showLocation: v, groupByLoc: v ? header.groupByLoc : false }) },
    { key: 'editChargeable', label: 'Edit Chargeable', short: 'CHRG', checked: header.editChargeable === true,
      toggle: (v) => (onToggleEditChargeable ? onToggleEditChargeable(v) : set({ editChargeable: v })) }
  ];

  return (
    <div className="pf-ctrl flex-1 min-w-[340px] px-3 py-2 flex flex-col border-r border-slate-200">
      <style>{CTRL_CSS}</style>

      {/* ── Title band — aligns with Bill To / Ship To header rows so all three
             columns share one baseline and one height. Line Options live here as
             a context (dropdown) menu, mirroring Ship To's header-right action. ── */}
      <div className="flex items-center gap-2 mb-2.5 pb-1.5 border-b border-slate-200">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 text-[#1877F2]">
          <ControlOutlined style={{ fontSize: 13 }} />
        </span>
        <span className="text-[14px] font-bold text-slate-800 tracking-tight">New Proforma</span>
      </div>

      {/* Body: Identity + tolerance faceplate, top-aligned (no bottom-flush
          stretch) so the faceplate sits directly under the identity cards with
          no gap on top. Line Options now live in the title-band menu. */}
      <div className="flex-1 flex flex-col gap-3.5">

      {/* ── Identity: machine-issued Proforma No + Date ── */}
      <div className="flex items-stretch gap-2.5">
        <div className="pf-idcard pf-docno pf-date">
          <span className="pf-k">Proforma No</span>
          <Input size="small" className="font-bold" value={header.proformaNo || ''} placeholder="Auto on save" disabled readOnly />
        </div>
        <div className="pf-idcard pf-date">
          <span className="pf-k">Date</span>
          <div className="flex items-center gap-1.5">
            <CalendarOutlined className="text-slate-400 text-[12px]" />
            <Input type="date" size="small" style={{ width: 128 }} value={header.proformaDate || ''} disabled={readOnly}
              onChange={(e) => set({ proformaDate: e.target.value })} />
          </div>
        </div>
      </div>

      {/* ── Cutting-spec plate — governing measurement instrument for the doc.
             Hairline-celled zones, stacked in order of authority. Visual only;
             every control keeps its original value/handler. ── */}
      <div>
        <div className="pf-plate">
          {/* Zone 1 — wastage mode + W/H tolerance share one line (accent tick) */}
          <div className="pf-zone pf-zone-mode">
            <span className="pf-zlabel">Wastage Mode</span>
            <div className="pf-modeline">
              <Select size="small" className="pf-wst" popupClassName="pf-wst-pop" popupMatchSelectWidth={false}
                style={{ width: 118 }} disabled={readOnly} value={mode}
                options={wastageOptions} onChange={(v) => set({ wastageMode: v })} />
              <span className="pf-dim"><span className="pf-lab">W</span>{wastageCell('wastageWidthMm')}</span>
              <span className="pf-dim"><span className="pf-lab">H</span>{wastageCell('wastageHeightMm')}</span>
              {isPlus ? <span className="pf-unit-chip">mm</span> : null}
            </div>
          </div>

          {/* Zone 1b — the DOCUMENT's price list (migration 0185).
                 It sits on this plate rather than beside the customer because
                 it is the same KIND of control as Wastage Mode: a per-document
                 instrument that governs how every line is valued. Beside the
                 customer it read as a property OF the customer, which is the
                 one thing it is not — changing it here never touches the
                 customer's own mapping. */}
          <div className="pf-zone pf-zone-pl">
            <span className="pf-zlabel">Price List</span>
            <PriceListPicker header={header} setHeader={setHeader} readOnly={readOnly} />
          </div>

          {/* Zone 2 — UOM line (input unit + area-unit pills) + line-option pills */}
          <div className="pf-zone pf-zone-tracks">
            <div className="pf-uomline">
              <div className="pf-areagroup">
              {areaRenderOrder.map((tag) => {
                const isPriced = tag === uomArea;               // locked, always on
                const checked = isPriced ? true : areaVisible.includes(tag);
                const cls = isPriced ? 'pf-areatag lock' : `pf-areatag${checked ? ' on' : ''}${readOnly ? ' dis' : ''}`;
                const title = isPriced ? 'Priced unit — locked to Company Defaults'
                  : tag === secondaryArea ? 'Reference area column (display only)' : undefined;
                return (
                  <span key={tag} className={cls} onClick={() => toggleArea(tag)} title={title}
                    role={isPriced ? undefined : 'checkbox'} aria-checked={isPriced ? undefined : checked}>
                    {tag}{isPriced ? <LockOutlined /> : null}
                  </span>
                );
              })}
              </div>
              <Select size="small" className="pf-ipunit" value={header.inputUnitCode || 'MM'} disabled={readOnly}
                options={opts(dropdowns.inputUnits)} onChange={(v) => set({ inputUnitCode: v })} />
            </div>
            <div className="pf-areagroup">
              {OPTIONS.map((o) => (
                <span key={o.key}
                  className={`pf-areatag${o.checked ? ' on' : ''}${readOnly ? ' dis' : ''}`}
                  role="checkbox" aria-checked={o.checked} title={o.label}
                  onClick={() => { if (!readOnly) o.toggle(!o.checked); }}>
                  {o.short}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Price UOM + Co. State are configured once in Masters → Company Defaults
          and seeded onto every new Proforma; the per-document controls were removed
          here. The values still ride on the header (priceUomCode / companyStateCode)
          and persist on save. */}
      </div>
    </div>
  );
}
